import qs from "qs";

import { DEFAULT_QUERY_OPERATORS } from "./constants";
import { isPlainObject, setNestedPathValue } from "./common";

const parseScalarQueryValue = value => {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return Number(value);
  }
  return value;
};

const ARRAY_OPERATORS = ["in", "notIn"];

const parseQueryParamValue = ({ rawValue, operator }) => {
  if (ARRAY_OPERATORS.includes(operator)) {
    return rawValue
      .split(",")
      .filter(item => item !== "")
      .map(parseScalarQueryValue);
  }
  return parseScalarQueryValue(rawValue);
};

const toSerializableValue = item => {
  if (
    isPlainObject(item) &&
    Object.prototype.hasOwnProperty.call(item, "value") &&
    !isPlainObject(item.value) &&
    !Array.isArray(item.value)
  ) {
    return item.value;
  }
  return item;
};

const formatQueryParamValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => String(toSerializableValue(item))).join(",");
  }
  return String(toSerializableValue(value));
};

const normalizeLocationSearch = (locationSearch = "") => {
  if (locationSearch.startsWith("?")) {
    return locationSearch.slice(1);
  }
  return locationSearch;
};

const normalizeParsedQueryValue = value => {
  if (Array.isArray(value)) {
    return value[value.length - 1];
  }
  return value;
};

const toQueryObject = currentParams => {
  if (currentParams instanceof URLSearchParams) {
    return Array.from(currentParams.entries()).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {},
    );
  }

  if (typeof currentParams === "string") {
    return qs.parse(normalizeLocationSearch(currentParams));
  }

  if (isPlainObject(currentParams)) {
    return currentParams;
  }

  return {};
};

const minimallyEncodeQueryValue = value => {
  return encodeURIComponent(String(value)).replace(/%2C/gi, ",");
};

const parseFilterFromLocationSearch = ({
  locationSearch = "",
  operators = DEFAULT_QUERY_OPERATORS,
  reservedKeys = [],
} = {}) => {
  const parsedQuery = qs.parse(normalizeLocationSearch(locationSearch));
  const parsedFilters = Object.entries(parsedQuery)
    .filter(([key]) => !reservedKeys.includes(key))
    .reduce((acc, [key, rawValue]) => {
      const segments = key.split(".");
      const possibleOperator = segments[segments.length - 1];
      const hasOperator = operators.includes(possibleOperator);
      const path = (hasOperator ? segments.slice(0, -1) : segments).join(".");
      const normalizedRawValue = normalizeParsedQueryValue(rawValue);

      if (!path || typeof normalizedRawValue === "undefined") {
        return acc;
      }

      const scalarRawValue =
        typeof normalizedRawValue === "string"
          ? normalizedRawValue
          : String(normalizedRawValue);

      const parsedValue = hasOperator
        ? {
          operator: possibleOperator,
          ...(possibleOperator === "isNull"
            ? {}
            : {
              value: parseQueryParamValue({
                rawValue: scalarRawValue,
                operator: possibleOperator,
              }),
            }),
        }
        : parseQueryParamValue({ rawValue: scalarRawValue });

      return setNestedPathValue(acc, path, parsedValue);
    }, {});
  return Object.keys(parsedFilters).length > 0 ? parsedFilters : null;
};

const collectFilterEntries = (value, path = "") => {
  if (!isPlainObject(value)) {
    return path ? { [path]: formatQueryParamValue(value) } : {};
  }

  if (typeof value.operator === "string") {
    const operatorKey = `${path}.${value.operator}`;
    if (value.operator === "isNull") {
      return { [operatorKey]: "true" };
    }
    if (value.value == null || value.value === "") {
      return {};
    }
    return { [operatorKey]: formatQueryParamValue(value.value) };
  }

  return Object.entries(value).reduce((acc, [key, nestedValue]) => {
    const nestedPath = path ? `${path}.${key}` : key;
    return { ...acc, ...collectFilterEntries(nestedValue, nestedPath) };
  }, {});
};

const serializeFilterToQueryParams = ({
  currentParams = {},
  searchInput,
  reservedKeys = [],
} = {}) => {
  const baseParams = toQueryObject(currentParams);
  const nextParams = Object.entries(baseParams).reduce((acc, [key, value]) => {
    if (reservedKeys.includes(key)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  return qs.stringify({ ...nextParams, ...collectFilterEntries(searchInput) }, {
    encodeValuesOnly: true,
    encoder: (value, defaultEncoder, charset, type) => {
      if (type === "value") {
        return minimallyEncodeQueryValue(value);
      }
      return defaultEncoder(value, defaultEncoder, charset, type);
    },
  });
};

export { parseFilterFromLocationSearch, serializeFilterToQueryParams };
